import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StatusBarComponent } from '../status-bar/status-bar.component';

/** The USSD prompt as a handset shows it: the menu text, a reply field, Cancel and Send. */
@Component({
  selector: 'app-ussd-dialog',
  standalone: true,
  imports: [FormsModule, StatusBarComponent],
  templateUrl: './ussd-dialog.component.html',
  styleUrl: './ussd-dialog.component.css',
})
export class UssdDialogComponent implements OnChanges {
  @Input() message: string = '';
  @Input() dialedCode: string = '';
  @Input() isLoading: boolean = false;
  @Input() isSessionEnd: boolean = false;
  @Input() isError: boolean = false;
  @Input() carrier: string = 'Test network';

  @Output() send = new EventEmitter<string>();
  @Output() cancel = new EventEmitter<void>();
  @Output() home = new EventEmitter<void>();

  @ViewChild('replyInput') private replyInput?: ElementRef<HTMLInputElement>;
  @ViewChild('okBtn') private okBtn?: ElementRef<HTMLButtonElement>;
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  userInput: string = '';

  get awaitingReply(): boolean {
    return !this.isLoading && !this.isSessionEnd && !this.isError;
  }

  /** What the live region reads after each turn: running, the menu, the end or the error. */
  get announcement(): string {
    if (this.isLoading) return 'USSD code running…';
    const text = this.message || (this.isSessionEnd ? 'Session ended.' : '');
    return this.isError ? `Request failed. ${text}`.trim() : text;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isBrowser || this.isLoading) return;
    if (!(changes['message'] || changes['isLoading'] || changes['isSessionEnd'] || changes['isError'])) return;
    // Send and the reply field are gone while the request runs, so focus
    // would drop to the page. A new menu puts the cursor in the reply field,
    // as a handset does; an end or error screen focuses OK.
    setTimeout(() => {
      const target = this.awaitingReply ? this.replyInput : this.okBtn;
      target?.nativeElement.focus({ preventScroll: true });
    });
  }

  handleSend(): void {
    if (this.userInput.trim() || this.isSessionEnd) {
      this.send.emit(this.userInput);
      this.userInput = '';
    }
  }

  onReplyKeydown(event: Event): void {
    const e = event as KeyboardEvent;
    if (e.key === 'Enter') {
      e.preventDefault();
      this.handleSend();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.cancel.emit();
    }
  }
}
